/**
 * cd-custom.js — Phase 3 Custom JS modules
 * - Cart drawer open/close (Phase 2 A.4)
 * - Collection filters AJAX + Price slider (Phase 2 A.3)
 */

(function() {
  'use strict';

  /**
   * Cart Drawer Module — open/close behavior
   */
  const CartDrawer = {
    drawer: null,
    backdrop: null,
    trigger: null,

    init() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.backdrop = document.querySelector('[data-cart-drawer-backdrop]');
      this.trigger = document.querySelector('[data-cart-drawer-trigger]');
      if (!this.drawer) return;

      document.addEventListener('click', (e) => this.handleClick(e), true);
      document.addEventListener('keydown', (e) => this.handleKeydown(e));
    },

    handleClick(e) {
      const triggerBtn = e.target.closest('[data-cart-drawer-trigger]');
      const closeBtn = e.target.closest('[data-cart-drawer-close]');
      const backdropEl = e.target.closest('[data-cart-drawer-backdrop]');

      if (triggerBtn) {
        e.preventDefault();
        e.stopPropagation();
        this.open();
      } else if (closeBtn || backdropEl) {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      }
    },

    handleKeydown(e) {
      const d = document.querySelector('[data-cart-drawer]');
      if (e.key === 'Escape' && d && d.classList.contains('is-open')) {
        this.close();
      }
    },

    open() {
      const d = document.querySelector('[data-cart-drawer]');
      const bd = document.querySelector('[data-cart-drawer-backdrop]');
      if (!d) return;
      d.classList.add('is-open');
      d.setAttribute('aria-hidden', 'false');
      if (bd) bd.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    },

    close() {
      const d = document.querySelector('[data-cart-drawer]');
      const bd = document.querySelector('[data-cart-drawer-backdrop]');
      if (d) {
        d.classList.remove('is-open');
        d.setAttribute('aria-hidden', 'true');
      }
      if (bd) bd.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  };

  /**
   * Collection Filters Module (Phase 2 A.3)
   * AJAX-driven filtering: intercepts clicks on filter links / pills / pagination /
   * sort dropdown / price slider submit, fetches via Section Rendering API,
   * replaces the section DOM in place. No URL change, no page refresh.
   */
  const CollectionFilters = {
    section: null,
    pendingRequest: null,
    currentUrl: null,

    init() {
      this.section = document.querySelector('[data-cd-collection-section]');
      if (!this.section) return;
      this.currentUrl = this.currentUrl || (this.section.dataset.collectionUrl + window.location.search);

      if (!this._listenersBound) {
        document.addEventListener('click', (e) => this.handleClick(e), true);
        document.addEventListener('change', (e) => this.handleChange(e), true);
        document.addEventListener('submit', (e) => this.handleSubmit(e), true);
        window.addEventListener('popstate', (e) => this.handlePopstate(e));
        this._listenersBound = true;
      }
    },

    handleClick(e) {
      if (!this.section || !this.section.contains(e.target)) return;
      const link = e.target.closest('.collection-filters__field--link, .active-filter:not(.active-filter--collection), .pagination a, .page-link');
      if (!link || !link.href) return;
      if (link.classList.contains('active-filter--collection')) return;

      const href = link.href;
      // Category links target a different collection (no ?filter.* and a /collections/handle URL).
      // We AJAX-fetch + replace hero + grid AND pushState the URL so the address bar
      // stays in sync with what the user sees, without a hard reload.
      const isCategoryNav = link.classList.contains('collection-filters__field--link')
          && href.indexOf('filter.') === -1
          && /\/collections\/[^/?#]+\/?$/i.test(href.split('?')[0]);

      e.preventDefault();
      e.stopPropagation();
      this.applyUrl(href, { pushUrl: isCategoryNav });
    },

    handlePopstate(e) {
      // Replay the URL the browser navigated to (back/forward) without another pushState
      const url = (e.state && e.state.cdUrl) || window.location.href;
      this.applyUrl(url, { pushUrl: false });
    },

    handleChange(e) {
      if (!this.section || !this.section.contains(e.target)) return;
      const sortSelect = e.target.closest('[data-cd-sort], .collection-sort__select');
      if (!sortSelect) return;
      e.preventDefault();
      e.stopPropagation();
      const base = this.currentUrl || (this.section.dataset.collectionUrl);
      const u = new URL(base, window.location.origin);
      if (sortSelect.value) {
        u.searchParams.set('sort_by', sortSelect.value);
      } else {
        u.searchParams.delete('sort_by');
      }
      this.applyUrl(u.toString());
    },

    handleSubmit(e) {
      if (!this.section || !this.section.contains(e.target)) return;
      const form = e.target.closest('[data-price-filter]');
      if (!form) return;
      e.preventDefault();
      const collectionUrl = this.section.dataset.collectionUrl;
      const params = new URLSearchParams(new FormData(form));
      this.applyUrl(collectionUrl + '?' + params.toString());
    },

    async applyUrl(targetUrl, options) {
      if (!this.section) return;
      const opts = options || {};
      const pushUrl = opts.pushUrl === true;

      const u = new URL(targetUrl, window.location.origin);
      const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (preview && !u.searchParams.has('preview_theme_id')) {
        u.searchParams.set('preview_theme_id', preview);
      }

      if (this.pendingRequest) this.pendingRequest.abort();
      const controller = new AbortController();
      this.pendingRequest = controller;

      this.section.setAttribute('aria-busy', 'true');
      this.section.style.opacity = '0.55';

      try {
        const r = await fetch(u.toString(), { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!r.ok) throw new Error('HTTP ' + r.status);
        const html = await r.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newSection = doc.querySelector('[data-cd-collection-section]');
        if (!newSection) throw new Error('no [data-cd-collection-section] in fetched HTML');

        // Replace grid + sidebar
        this.section.replaceWith(newSection);
        this.section = newSection;

        // Replace hero (breadcrumb + H1 + description) so it matches the AJAX-fetched collection
        const currentHero = document.querySelector('.collection-hero');
        const newHero = doc.querySelector('.collection-hero');
        if (currentHero && newHero) {
          currentHero.replaceWith(newHero);
        }

        // Update document title from fetched page
        const fetchedTitle = doc.querySelector('title');
        if (fetchedTitle && fetchedTitle.textContent) {
          document.title = fetchedTitle.textContent;
        }

        this.currentUrl = targetUrl;
        PriceFilter.init();

        // Sync browser URL with AJAX state so back-button restores pagination/filters.
        // pushState for category navigation (genuine new screen), replaceState for refinements
        // (filters, sort, pagination within the same collection).
        const pushed = new URL(targetUrl, window.location.origin);
        const newUrl = pushed.pathname + pushed.search;
        if (pushUrl) {
          window.history.pushState({ cdUrl: targetUrl }, '', newUrl);
        } else if (newUrl !== window.location.pathname + window.location.search) {
          window.history.replaceState({ cdUrl: targetUrl }, '', newUrl);
        }

        const top = (document.querySelector('.collection-hero') || this.section).getBoundingClientRect().top + window.scrollY - 120;
        if (top < window.scrollY) {
          window.scrollTo({ top, behavior: 'smooth' });
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('CollectionFilters AJAX failed', err);
      } finally {
        this.pendingRequest = null;
        if (this.section) {
          this.section.removeAttribute('aria-busy');
          this.section.style.opacity = '';
        }
      }
    }
  };

  /**
   * Price Range Slider Module (Phase 2 A.3)
   * Drag handles -> update positions + display values
   * On release -> dispatch submit event on parent form (intercepted by CollectionFilters)
   */
  const PriceFilter = {
    init() {
      const tracks = document.querySelectorAll('.price-filter-range');
      tracks.forEach(track => {
        if (track.dataset.cdInit === '1') return;
        this.bind(track);
        track.dataset.cdInit = '1';
      });
    },

    bind(track) {
      const form = track.closest('[data-price-filter]');
      if (!form) return;

      const rangeMin = parseInt(track.dataset.rangeMin, 10) || 0;
      const rangeMax = parseInt(track.dataset.rangeMax, 10) || 100000;
      let currentMin = parseInt(track.dataset.currentMin, 10);
      let currentMax = parseInt(track.dataset.currentMax, 10);
      if (isNaN(currentMin)) currentMin = rangeMin;
      if (isNaN(currentMax)) currentMax = rangeMax;
      currentMin = Math.max(rangeMin, Math.min(currentMin, rangeMax));
      currentMax = Math.max(rangeMin, Math.min(currentMax, rangeMax));

      const handleLo = form.querySelector('[data-handle="lo"]');
      const handleHi = form.querySelector('[data-handle="hi"]');
      const inputLo = form.querySelector('[data-price-input="lo"]');
      const inputHi = form.querySelector('[data-price-input="hi"]');
      const displayLo = form.querySelector('[data-price-display="lo"]');
      const displayHi = form.querySelector('[data-price-display="hi"]');

      const state = { currentMin, currentMax, rangeMin, rangeMax };
      const refs = { track, handleLo, handleHi, inputLo, inputHi, displayLo, displayHi, form };

      this.render(state, refs);

      [handleLo, handleHi].forEach(h => {
        if (!h) return;
        h.addEventListener('pointerdown', e => this.startDrag(e, h, state, refs));
      });
    },

    startDrag(e, handle, state, refs) {
      e.preventDefault();
      e.stopPropagation();
      try { handle.setPointerCapture(e.pointerId); } catch (_) {}
      const which = handle.dataset.handle;
      const initialMin = state.currentMin;
      const initialMax = state.currentMax;
      const span = state.rangeMax - state.rangeMin;
      const minGap = Math.max(100, Math.round(span * 0.02));

      const onMove = (ev) => {
        const rect = refs.track.getBoundingClientRect();
        const clientX = ev.clientX != null ? ev.clientX : (ev.touches && ev.touches[0] ? ev.touches[0].clientX : 0);
        const x = clientX - rect.left;
        const pct = Math.min(1, Math.max(0, x / rect.width));
        const value = Math.round(state.rangeMin + pct * span);

        if (which === 'lo') {
          state.currentMin = Math.min(value, state.currentMax - minGap);
          state.currentMin = Math.max(state.currentMin, state.rangeMin);
        } else {
          state.currentMax = Math.max(value, state.currentMin + minGap);
          state.currentMax = Math.min(state.currentMax, state.rangeMax);
        }
        this.render(state, refs);
      };

      const onUp = () => {
        try { handle.releasePointerCapture(e.pointerId); } catch (_) {}
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onUp);
        if (state.currentMin !== initialMin || state.currentMax !== initialMax) {
          if (typeof refs.form.requestSubmit === 'function') {
            refs.form.requestSubmit();
          } else {
            refs.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        }
      };

      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onUp);
    },

    render(state, refs) {
      const span = state.rangeMax - state.rangeMin || 1;
      const pctLo = ((state.currentMin - state.rangeMin) / span) * 100;
      const pctHi = ((state.currentMax - state.rangeMin) / span) * 100;
      if (refs.handleLo) refs.handleLo.style.left = pctLo + '%';
      if (refs.handleHi) refs.handleHi.style.left = pctHi + '%';
      // Shopify expects URL price params in major-currency units (euros), Liquid exposes values in cents.
      if (refs.inputLo) refs.inputLo.value = Math.round(state.currentMin / 100);
      if (refs.inputHi) refs.inputHi.value = Math.round(state.currentMax / 100);
      refs.track.style.setProperty('--connect-lo', pctLo + '%');
      refs.track.style.setProperty('--connect-hi', pctHi + '%');
      if (refs.displayLo) refs.displayLo.textContent = this.formatMoney(state.currentMin);
      if (refs.displayHi) refs.displayHi.textContent = this.formatMoney(state.currentMax);
    },

    formatMoney(cents) {
      const euros = Math.round(cents / 100);
      return '€' + euros;
    }
  };

  const CartPage = {
    init() {
      const section = document.querySelector('[data-cd-cart-section]');
      if (!section || section.__cdCartBound) return;
      section.__cdCartBound = true;
      section.addEventListener('click', this.onClick.bind(this));
    },

    onClick(e) {
      const stepBtn = e.target.closest('[data-qty-step]');
      if (stepBtn) {
        e.preventDefault();
        const lineEl = stepBtn.closest('[data-cart-line]');
        const input = lineEl && lineEl.querySelector('[data-qty-input]');
        if (!input) return;
        const step = parseInt(stepBtn.getAttribute('data-qty-step'), 10);
        const next = Math.max(0, parseInt(input.value, 10) + step);
        this.setLineQty(lineEl, next);
        return;
      }
      const removeLink = e.target.closest('.cd-cart-line__remove');
      if (removeLink) {
        e.preventDefault();
        const lineEl = removeLink.closest('[data-cart-line]');
        this.setLineQty(lineEl, 0);
      }
    },

    formatMoney(cents) {
      const sign = cents < 0 ? '-' : '';
      const abs = Math.abs(cents);
      const e = Math.floor(abs / 100);
      const c = abs % 100;
      return `${sign}€${e},${c.toString().padStart(2, '0')}`;
    },

    setLineQty(lineEl, qty) {
      const input = lineEl.querySelector('[data-qty-input]');
      const lineIndex = parseInt(input.getAttribute('data-line'), 10);
      const unitPrice = parseInt(lineEl.getAttribute('data-unit-price'), 10);
      input.value = qty;

      if (qty === 0) {
        lineEl.style.transition = 'opacity .2s ease, max-height .25s ease, margin .2s ease, padding .2s ease';
        lineEl.style.opacity = '0';
        lineEl.style.maxHeight = lineEl.offsetHeight + 'px';
        requestAnimationFrame(() => {
          lineEl.style.maxHeight = '0';
          lineEl.style.padding = '0';
          lineEl.style.margin = '0';
        });
        setTimeout(() => lineEl.remove(), 220);
      } else {
        const linePriceEl = lineEl.querySelector('[data-line-price]');
        if (linePriceEl) linePriceEl.textContent = this.formatMoney(unitPrice * qty);
      }

      // Defer DOM total recomputation so the removed line is out of the tree
      setTimeout(() => this.recomputeTotals(), qty === 0 ? 230 : 0);
      this.postChange(lineIndex, qty);
    },

    recomputeTotals() {
      const lines = document.querySelectorAll('[data-cart-line]');
      let subtotal = 0;
      let count = 0;
      lines.forEach((l) => {
        const u = parseInt(l.getAttribute('data-unit-price'), 10) || 0;
        const q = parseInt(l.querySelector('[data-qty-input]').value, 10) || 0;
        subtotal += u * q;
        count += q;
      });

      const fmt = this.formatMoney.bind(this);
      const $ = (sel) => document.querySelector(sel);

      const subtotalEl = $('[data-cart-subtotal]');
      if (subtotalEl) subtotalEl.textContent = fmt(subtotal);

      // Total = subtotal (Shopify taxes are included in items, no extra add)
      const totalEl = $('[data-cart-total]');
      if (totalEl) totalEl.textContent = fmt(subtotal);

      // Header count
      const countEl = $('[data-cart-count]');
      if (countEl) countEl.textContent = String(count);
      const labelEl = $('[data-cart-count-label]');
      if (labelEl) labelEl.textContent = count === 1 ? 'item' : 'items';

      // Free shipping note + estimated shipping
      const summary = $('.cd-cart-summary');
      const threshold = summary ? parseInt(summary.getAttribute('data-free-ship-threshold'), 10) : 6000;
      const remaining = threshold - subtotal;
      const shipEl = $('[data-cart-shipping]');
      const noteEl = $('[data-cart-shipping-note]');
      if (remaining > 0) {
        if (shipEl) shipEl.textContent = fmt(remaining) + ' to free EU';
        if (noteEl) {
          noteEl.innerHTML = 'Add <span class="accent">' + fmt(remaining) + '</span> for free EU shipping. Delivered in 3 to 6 business days by DHL. Duties pre-paid at checkout for US, UK and Canada.';
        }
      } else {
        if (shipEl) shipEl.textContent = 'Free';
        if (noteEl) {
          noteEl.innerHTML = '<span class="accent">Free EU shipping</span> applied. Delivered in 3 to 6 business days by DHL. Duties pre-paid at checkout for US, UK and Canada.';
        }
      }

      // Header cart icon count
      const headerCount = document.querySelector('.header-cart__count');
      if (headerCount) headerCount.textContent = '(' + count + ')';

      // If empty, switch to empty state
      if (count === 0) this.showEmptyState();
    },

    showEmptyState() {
      const section = document.querySelector('[data-cd-cart-section]');
      if (!section) return;
      const form = section.querySelector('.cd-cart-form');
      const trust = section.querySelector('.cd-cart-trust');
      if (form) form.remove();
      if (trust) trust.remove();
      const head = section.querySelector('.cd-cart-page__count');
      if (head) head.remove();
      const empty = document.createElement('div');
      empty.className = 'cd-cart-empty';
      const s = (window.cdStrings && window.cdStrings.cart) || {};
      const emptyLabel = s.empty || 'Your cart is empty.';
      const browseLabel = s.browse_collection || 'Browse the collection';
      empty.innerHTML = '<p class="cd-cart-empty__lead">' + emptyLabel + '</p>' +
        '<a href="/collections/all" class="cd-cart-empty__cta">' + browseLabel + '</a>';
      section.appendChild(empty);
    },

    postChange(line, quantity) {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line, quantity }),
        credentials: 'same-origin',
      }).catch((err) => console.error('Cart sync failed', err));
    },
  };

  const DrawerLines = {
    formatMoney(cents) {
      const sign = cents < 0 ? '-' : '';
      const abs = Math.abs(cents);
      const e = Math.floor(abs / 100);
      const c = abs % 100;
      return `${sign}€${e},${c.toString().padStart(2, '0')}`;
    },

    init() {
      const drawer = document.querySelector('[data-cart-drawer]');
      if (!drawer || drawer.__cdDrawerLinesBound) return;
      drawer.__cdDrawerLinesBound = true;
      drawer.addEventListener('click', this.onClick.bind(this));
    },

    onClick(e) {
      const stepBtn = e.target.closest('[data-qty-step]');
      if (stepBtn) {
        e.preventDefault();
        const lineEl = stepBtn.closest('[data-cart-line]');
        const input = lineEl && lineEl.querySelector('[data-qty-input]');
        if (!input) return;
        const step = parseInt(stepBtn.getAttribute('data-qty-step'), 10);
        const next = Math.max(0, parseInt(input.value, 10) + step);
        this.setLineQty(lineEl, next);
        return;
      }
      const removeBtn = e.target.closest('[data-line-remove]');
      if (removeBtn) {
        e.preventDefault();
        const lineEl = removeBtn.closest('[data-cart-line]');
        this.setLineQty(lineEl, 0);
        return;
      }
      const upsellBtn = e.target.closest('[data-upsell-add]');
      if (upsellBtn) {
        e.preventDefault();
        this.addUpsell(upsellBtn);
      }
    },

    setLineQty(lineEl, qty) {
      const input = lineEl.querySelector('[data-qty-input]');
      const lineIndex = parseInt(input.getAttribute('data-line'), 10);
      const unitPrice = parseInt(lineEl.getAttribute('data-unit-price'), 10);
      input.value = qty;

      if (qty === 0) {
        lineEl.style.transition = 'opacity .2s ease, max-height .25s ease, margin .2s ease, padding .2s ease';
        lineEl.style.opacity = '0';
        lineEl.style.maxHeight = lineEl.offsetHeight + 'px';
        requestAnimationFrame(() => {
          lineEl.style.maxHeight = '0';
          lineEl.style.padding = '0';
          lineEl.style.margin = '0';
        });
        setTimeout(() => lineEl.remove(), 220);
      } else {
        const lp = lineEl.querySelector('[data-line-price]');
        if (lp) lp.textContent = this.formatMoney(unitPrice * qty);
      }
      setTimeout(() => this.recomputeTotals(), qty === 0 ? 230 : 0);
      this.postChange(lineIndex, qty);
    },

    recomputeTotals() {
      const drawer = document.querySelector('[data-cart-drawer]');
      if (!drawer) return;
      const lines = drawer.querySelectorAll('[data-cart-line]');
      let subtotal = 0, count = 0;
      lines.forEach((l) => {
        const u = parseInt(l.getAttribute('data-unit-price'), 10) || 0;
        const q = parseInt(l.querySelector('[data-qty-input]').value, 10) || 0;
        subtotal += u * q;
        count += q;
      });
      const fmt = this.formatMoney.bind(this);

      const sub = drawer.querySelector('[data-drawer-subtotal]');
      if (sub) sub.textContent = fmt(subtotal);

      const threshold = parseInt(drawer.getAttribute('data-shipping-threshold'), 10) || 6000;
      const remaining = threshold - subtotal;
      const text = drawer.querySelector('[data-shipping-text]');
      const fill = drawer.querySelector('[data-shipping-fill]');
      if (text) {
        text.innerHTML = remaining > 0
          ? `Add <strong>${fmt(remaining)}</strong> more for free EU shipping.`
          : 'You qualify for <strong>free EU shipping</strong>!';
      }
      if (fill) {
        const pct = Math.min(100, Math.max(0, Math.round((subtotal * 100) / threshold)));
        fill.style.width = pct + '%';
      }

      const bubble = document.querySelector('[data-cart-count-bubble]');
      if (bubble) bubble.textContent = '(' + count + ')';

      if (count === 0) this.showEmptyState(drawer);

      // Update cart page if open
      if (window.CartPage && document.querySelector('[data-cd-cart-section]')) {
        try { window.CartPage.recomputeTotals(); } catch (_) {}
      }
    },

    showEmptyState(drawer) {
      const inner = drawer.querySelector('[data-drawer-inner]');
      const footer = drawer.querySelector('.ajaxcart__footer');
      const shipBar = drawer.querySelector('[data-shipping-bar]');
      if (shipBar) shipBar.remove();
      if (footer) footer.remove();
      if (inner) {
        const s = (window.cdStrings && window.cdStrings.cart) || {};
        const emptyLabel = s.empty || 'Your cart is empty.';
        const continueLabel = s.continue_shopping || 'Continue shopping';
        inner.innerHTML = '<div class="ajaxcart__empty"><p class="ajaxcart__empty-lead">' + emptyLabel + '</p><a href="/collections/all" class="ajaxcart__empty-cta">' + continueLabel + '</a></div>';
      }
    },

    postChange(line, quantity) {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line, quantity }),
        credentials: 'same-origin',
      }).catch((err) => console.error('Drawer sync failed', err));
    },

    addUpsell(btn) {
      const variantId = btn.getAttribute('data-upsell-add');
      if (!variantId) return;
      const original = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
        credentials: 'same-origin',
      })
        .then((r) => r.json())
        .then(() => fetch(window.location.pathname + window.location.search, { credentials: 'same-origin' }))
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newDrawer = doc.querySelector('[data-cart-drawer]');
          const newBackdrop = doc.querySelector('[data-cart-drawer-backdrop]');
          const curDrawer = document.querySelector('[data-cart-drawer]');
          const curBackdrop = document.querySelector('[data-cart-drawer-backdrop]');
          if (newDrawer && curDrawer) {
            newDrawer.classList.add('is-open');
            newDrawer.setAttribute('aria-hidden', 'false');
            curDrawer.replaceWith(newDrawer);
          }
          if (newBackdrop && curBackdrop) {
            newBackdrop.classList.add('is-open');
            curBackdrop.replaceWith(newBackdrop);
          }
          document.body.style.overflow = 'hidden';
          // Re-bind delegated drawer listeners on the new node
          DrawerLines.init();
          // Sync header cart count bubble
          const bubble = document.querySelector('[data-cart-count-bubble]');
          const newBubble = doc.querySelector('[data-cart-count-bubble]');
          if (bubble && newBubble) bubble.textContent = newBubble.textContent;
        })
        .catch((err) => {
          console.error('Upsell add failed', err);
        })
        .finally(() => {
          // btn is now detached, no need to reset (drawer was replaced)
        });
    },
  };

  const PdpPrice = {
    formatMoney(cents) {
      const sign = cents < 0 ? '-' : '';
      const abs = Math.abs(cents);
      const e = Math.floor(abs / 100);
      const c = abs % 100;
      return `${sign}€${e},${c.toString().padStart(2, '0')}`;
    },
    update(root) {
      root = root || document;
      const btn = root.querySelector('[data-cd-add-btn]');
      const priceEl = root.querySelector('[data-cd-add-price]');
      if (!btn || !priceEl) return;
      const unit = parseInt(btn.getAttribute('data-unit-price-cents'), 10) || 0;
      const qtyInput = root.querySelector('[data-cd-qty-input]');
      const qty = Math.max(1, parseInt(qtyInput?.value, 10) || 1);
      let total = unit * qty;
      const standActive = root.querySelector('[data-cd-stand-row] [data-cd-stand="yes"].is-active');
      if (standActive) {
        total += parseInt(standActive.getAttribute('data-stand-price-cents'), 10) || 0;
      }
      priceEl.textContent = this.formatMoney(total);
    },
  };

  const ProductQty = {
    init(root) {
      root = root || document;
      const wrap = root.querySelector('[data-cd-qty-wrap]');
      if (!wrap || wrap.__cdQtyBound) return;
      wrap.__cdQtyBound = true;
      const formRoot = wrap.closest('[data-cd-qv-root], [data-cd-pdp-section]') || document;
      wrap.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-cd-qty-step]');
        if (!btn) return;
        e.preventDefault();
        const input = wrap.querySelector('[data-cd-qty-input]');
        if (!input) return;
        const step = parseInt(btn.getAttribute('data-cd-qty-step'), 10) || 0;
        const next = Math.max(1, (parseInt(input.value, 10) || 1) + step);
        input.value = next;
        const hidden = formRoot.querySelector('[data-cd-qty-hidden]');
        if (hidden) hidden.value = next;
        PdpPrice.update(formRoot);
      });
      wrap.addEventListener('input', (e) => {
        if (!e.target.matches('[data-cd-qty-input]')) return;
        const next = Math.max(1, parseInt(e.target.value, 10) || 1);
        e.target.value = next;
        const hidden = formRoot.querySelector('[data-cd-qty-hidden]');
        if (hidden) hidden.value = next;
        PdpPrice.update(formRoot);
      });
    },
  };

  const ProductTabs = {
    init() {
      const tabsRoot = document.querySelector('[data-cd-tabs]');
      if (!tabsRoot || tabsRoot.__cdTabsBound) return;
      tabsRoot.__cdTabsBound = true;
      tabsRoot.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-cd-tab]');
        if (!btn) return;
        e.preventDefault();
        const key = btn.getAttribute('data-cd-tab');
        tabsRoot.querySelectorAll('[data-cd-tab]').forEach((b) => b.classList.toggle('is-active', b === btn));
        tabsRoot.querySelectorAll('[data-cd-tab-panel]').forEach((p) => {
          p.classList.toggle('is-active', p.getAttribute('data-cd-tab-panel') === key);
        });
      });
    },
  };

  const ProductStand = {
    init(root) {
      root = root || document;
      const row = root.querySelector('[data-cd-stand-row]');
      if (!row || row.__cdStandBound) return;
      row.__cdStandBound = true;
      const formRoot = row.closest('[data-cd-qv-root], [data-cd-pdp-section]') || document;
      row.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-cd-stand]');
        if (!btn) return;
        e.preventDefault();
        row.querySelectorAll('[data-cd-stand]').forEach((b) => b.classList.toggle('is-active', b === btn));
        PdpPrice.update(formRoot);
      });
    },
    getActiveVariantId(root) {
      root = root || document;
      const active = root.querySelector('[data-cd-stand-row] [data-cd-stand="yes"].is-active');
      return active ? active.getAttribute('data-stand-variant') : null;
    },
  };

  const AddToCart = {
    init() {
      if (document.__cdAddToCartBound) return;
      document.__cdAddToCartBound = true;
      document.addEventListener('submit', this.onSubmit.bind(this), true);
    },

    onSubmit(e) {
      const form = e.target.closest('form');
      if (!form) return;
      const action = (form.getAttribute('action') || '').toLowerCase();
      if (!action.endsWith('/cart/add')) return;
      e.preventDefault();
      e.stopPropagation();

      const submitBtn = form.querySelector('[type="submit"]');
      const originalLabel = submitBtn ? submitBtn.textContent : '';
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-adding');
      }

      // Build items array — supports PDP and Quick View multi-add (product + optional stand)
      const formRoot = form.closest('[data-cd-qv-root], [data-cd-pdp-section]') || document;
      const isQuickView = !!form.closest('[data-cd-qv-root]');
      const variantId = form.querySelector('[name="id"]')?.value;
      const qty = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10) || 1;
      const items = variantId ? [{ id: parseInt(variantId, 10), quantity: qty }] : [];
      const standId = ProductStand.getActiveVariantId(formRoot);
      if (standId) items.push({ id: parseInt(standId, 10), quantity: 1 });

      const useItemsApi = items.length > 0;
      const body = useItemsApi
        ? JSON.stringify({ items })
        : new FormData(form);
      const headers = useItemsApi
        ? { 'Accept': 'application/json', 'Content-Type': 'application/json' }
        : { 'Accept': 'application/json' };

      fetch('/cart/add.js', {
        method: 'POST',
        headers,
        body,
        credentials: 'same-origin',
      })
        .then((r) => r.ok ? r.json() : Promise.reject(r))
        .then(() => fetch(window.location.pathname + window.location.search, { credentials: 'same-origin' }))
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newDrawer = doc.querySelector('[data-cart-drawer]');
          const newBackdrop = doc.querySelector('[data-cart-drawer-backdrop]');
          const curDrawer = document.querySelector('[data-cart-drawer]');
          const curBackdrop = document.querySelector('[data-cart-drawer-backdrop]');
          if (newDrawer && curDrawer) {
            newDrawer.classList.add('is-open');
            newDrawer.setAttribute('aria-hidden', 'false');
            curDrawer.replaceWith(newDrawer);
          }
          if (newBackdrop && curBackdrop) {
            newBackdrop.classList.add('is-open');
            curBackdrop.replaceWith(newBackdrop);
          }
          document.body.style.overflow = 'hidden';
          DrawerLines.init();
          const bubble = document.querySelector('[data-cart-count-bubble]');
          const newBubble = doc.querySelector('[data-cart-count-bubble]');
          if (bubble && newBubble) bubble.textContent = newBubble.textContent;
          // If submission came from the Quick View modal, close it (keep body lock for the drawer)
          if (isQuickView && window.__cdQuickView) {
            window.__cdQuickView.close({ keepBodyLock: true });
          }
        })
        .catch((err) => {
          console.error('Add to cart failed', err);
        })
        .finally(() => {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.classList.remove('is-adding');
            if (originalLabel) submitBtn.textContent = originalLabel;
          }
        });
    },
  };

  const RelatedShuffle = {
    init() {
      document.querySelectorAll('[data-cd-related-grid]').forEach((grid) => {
        if (grid.__cdRelatedDone) return;
        grid.__cdRelatedDone = true;
        const count = Math.max(1, parseInt(grid.getAttribute('data-cd-related-show'), 10) || 3);
        const cards = Array.from(grid.querySelectorAll('[data-cd-related-card]'));
        // Fisher-Yates shuffle
        for (let i = cards.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [cards[i], cards[j]] = [cards[j], cards[i]];
        }
        // Re-append shuffled order + reveal first N
        const frag = document.createDocumentFragment();
        cards.forEach((c) => { c.style.display = 'none'; frag.appendChild(c); });
        grid.innerHTML = '';
        grid.appendChild(frag);
        cards.slice(0, count).forEach((c) => { c.style.display = ''; });
      });
    },
  };

  const FormatSwap = {
    cache: new Map(),
    init() {
      if (document.__cdFormatSwapBound) return;
      document.__cdFormatSwapBound = true;
      // Prefetch on hover/focus — fills the cache so click is instant
      const prefetch = (link) => {
        const url = link.getAttribute('href');
        if (!url || this.cache.has(url)) return;
        const promise = fetch(url, { credentials: 'same-origin', headers: { 'Accept': 'text/html' } })
          .then((r) => r.ok ? r.text() : Promise.reject(r))
          .catch(() => { this.cache.delete(url); return null; });
        this.cache.set(url, promise);
      };
      document.addEventListener('mouseover', (e) => {
        const link = e.target.closest('[data-cd-format-swap]');
        if (link) prefetch(link);
      });
      document.addEventListener('focusin', (e) => {
        const link = e.target.closest('[data-cd-format-swap]');
        if (link) prefetch(link);
      });
      document.addEventListener('click', (e) => {
        const link = e.target.closest('[data-cd-format-swap]');
        if (!link) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        // Capture viewport position of clicked link — we'll keep it at the same Y after swap
        const anchorViewportY = link.getBoundingClientRect().top;
        // Optimistic active toggle in the Format row
        const row = link.closest('[data-cd-format-row]');
        if (row) {
          row.querySelectorAll('.variant-option').forEach((o) => o.classList.remove('is-active'));
          link.classList.add('is-active');
        }
        this.swap(link.getAttribute('href'), anchorViewportY);
      });
    },
    applyHtml(html, url, anchorViewportY) {
      const section = document.querySelector('[data-cd-pdp-section]');
      if (!section) { window.location.href = url; return; }
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-cd-pdp-section]');
      if (!next) { window.location.href = url; return; }
      const newTitle = doc.querySelector('title');
      if (newTitle) document.title = newTitle.textContent;
      const html_el = document.documentElement;
      // Cancel any previous lock so a new swap doesn't fight an old one
      if (this._lockId) clearInterval(this._lockId);
      if (this._releaseHandlers) this._releaseHandlers();
      // Pin height to avoid scroll-clamp during image reflow
      section.style.minHeight = section.offsetHeight + 'px';
      window.history.pushState({}, '', url);
      section.innerHTML = next.innerHTML;
      // Re-anchor: place the active sibling at the same viewport Y
      const reanchor = () => {
        const newActive = section.querySelector('[data-cd-format-row] .variant-option.is-active');
        if (!newActive || typeof anchorViewportY !== 'number') return;
        const currentTop = newActive.getBoundingClientRect().top;
        const delta = currentTop - anchorViewportY;
        if (Math.abs(delta) > 1) {
          html_el.scrollTop = (html_el.scrollTop || document.body.scrollTop || 0) + delta;
        }
      };
      reanchor();
      // Lock for ~1s, but bail immediately if user scrolls manually (wheel/touch/keyboard)
      let userScrolled = false;
      const bail = () => { userScrolled = true; };
      window.addEventListener('wheel', bail, { passive: true, once: true });
      window.addEventListener('touchstart', bail, { passive: true, once: true });
      window.addEventListener('keydown', bail, { once: true });
      this._releaseHandlers = () => {
        window.removeEventListener('wheel', bail);
        window.removeEventListener('touchstart', bail);
        window.removeEventListener('keydown', bail);
      };
      this._lockId = setInterval(() => {
        if (userScrolled) { clearInterval(this._lockId); this._lockId = null; return; }
        reanchor();
      }, 8);
      let frames = 0;
      const loop = () => {
        if (userScrolled) return;
        reanchor();
        if (++frames < 36) requestAnimationFrame(loop);
      };
      requestAnimationFrame(loop);
      setTimeout(() => {
        if (this._lockId) { clearInterval(this._lockId); this._lockId = null; }
        if (this._releaseHandlers) { this._releaseHandlers(); this._releaseHandlers = null; }
        section.style.minHeight = '';
      }, 1000);
      document.querySelectorAll('[data-cd-qty-wrap], [data-cd-tabs], [data-cd-stand-row]').forEach((el) => { delete el.__cdQtyBound; delete el.__cdTabsBound; delete el.__cdStandBound; });
      ProductQty.init();
      ProductTabs.init();
      ProductStand.init();
      PdpPrice.update();
    },
    swap(url, anchorViewportY) {
      const section = document.querySelector('[data-cd-pdp-section]');
      if (!section || !url) { window.location.href = url; return; }
      const cached = this.cache.get(url);
      if (cached) {
        Promise.resolve(cached)
          .then((html) => html ? this.applyHtml(html, url, anchorViewportY) : (window.location.href = url));
        return;
      }
      fetch(url, { credentials: 'same-origin', headers: { 'Accept': 'text/html' } })
        .then((r) => r.ok ? r.text() : Promise.reject(r))
        .then((html) => this.applyHtml(html, url, anchorViewportY))
        .catch(() => { window.location.href = url; });
    },
  };

  const SearchSort = {
    init() {
      if (document.__cdSearchSortBound) return;
      document.__cdSearchSortBound = true;
      document.addEventListener('change', (e) => {
        if (!e.target.matches('[data-cd-search-sort]')) return;
        const form = e.target.closest('[data-cd-search-sort-form]');
        if (form) form.submit();
      });
    },
  };

  const ArticleShare = {
    init() {
      if (document.__cdArticleShareBound) return;
      document.__cdArticleShareBound = true;
      document.addEventListener('click', (e) => {
        const link = e.target.closest('[data-cd-share-copy]');
        if (!link) return;
        e.preventDefault();
        const url = window.location.href;
        const reset = () => {
          link.textContent = link.dataset.cdShareCopyOriginal || 'Copy link';
        };
        if (!link.dataset.cdShareCopyOriginal) {
          link.dataset.cdShareCopyOriginal = link.textContent;
        }
        const fallback = () => {
          try {
            const ta = document.createElement('textarea');
            ta.value = url;
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
          } catch (_) {}
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).catch(fallback);
        } else {
          fallback();
        }
        link.textContent = 'Copied';
        clearTimeout(link.__cdShareTimer);
        link.__cdShareTimer = setTimeout(reset, 1800);
      });
    },
  };

  const FAQAccordion = {
    init() {
      if (document.__cdFAQBound) return;
      document.__cdFAQBound = true;
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-cd-faq-trigger]');
        if (!trigger) return;
        e.preventDefault();
        const item = trigger.closest('[data-cd-faq-item]');
        if (!item) return;
        const wasOpen = item.classList.contains('is-open');
        const root = item.closest('[data-cd-faq]') || document;
        // Close all siblings (single-open)
        root.querySelectorAll('[data-cd-faq-item]').forEach((it) => {
          it.classList.remove('is-open');
          const btn = it.querySelector('[data-cd-faq-trigger]');
          if (btn) btn.setAttribute('aria-expanded', 'false');
        });
        // Toggle current (re-open unless it was just open — clicking an open item closes it)
        if (!wasOpen) {
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    },
  };

  const QuickView = {
    cache: new Map(),

    localePrefix() {
      const seg = (window.location.pathname.split('/')[1] || '').toLowerCase();
      return ['de', 'es', 'it', 'pl', 'pt'].includes(seg) ? `/${seg}` : '';
    },

    init() {
      if (document.__cdQuickViewBound) return;
      document.__cdQuickViewBound = true;
      window.__cdQuickView = this;

      const prefetch = (handle) => {
        if (!handle || this.cache.has(handle)) return;
        const url = `${this.localePrefix()}/products/${handle}?view=quickview`;
        const promise = fetch(url, {
          credentials: 'same-origin',
          headers: { 'Accept': 'text/html' },
        })
          .then((r) => (r.ok ? r.text() : Promise.reject(r)))
          .catch(() => { this.cache.delete(handle); return null; });
        this.cache.set(handle, promise);
      };

      const prefetchFromEvent = (e) => {
        const trigger = e.target.closest('[data-cd-quickview-handle]');
        if (trigger) prefetch(trigger.getAttribute('data-cd-quickview-handle'));
        const fmt = e.target.closest('[data-cd-qv-format-swap]');
        if (fmt) prefetch(fmt.getAttribute('data-handle'));
      };
      document.addEventListener('mouseover', prefetchFromEvent);
      document.addEventListener('focusin', prefetchFromEvent);

      document.addEventListener('click', (e) => {
        // Open trigger
        const trigger = e.target.closest('[data-cd-quickview-handle]');
        if (trigger) {
          e.preventDefault();
          e.stopPropagation();
          this.open(trigger.getAttribute('data-cd-quickview-handle'));
          return;
        }

        // View full details — close modal then allow navigation
        const detailsLink = e.target.closest('[data-cd-qv-details]');
        if (detailsLink) {
          this.close();
          return;
        }

        // Close button
        if (e.target.closest('[data-cd-qv-close]')) {
          e.preventDefault();
          this.close();
          return;
        }

        // Backdrop click (only when clicking the backdrop itself, not the modal content)
        const backdrop = e.target.closest('[data-cd-qv-backdrop]');
        if (backdrop && !e.target.closest('[data-cd-qv-root]')) {
          this.close();
          return;
        }

        // Format swap inside modal — smooth swap (optimistic + innerHTML replace, no flash)
        const fmt = e.target.closest('[data-cd-qv-format-swap]');
        if (fmt) {
          e.preventDefault();
          this.swapFormat(fmt);
          return;
        }

        // Thumbnail switching
        const thumb = e.target.closest('[data-cd-qv-thumb]');
        if (thumb) {
          const root = thumb.closest('[data-cd-qv-root]');
          if (!root) return;
          const main = root.querySelector('[data-cd-qv-main]');
          const full = thumb.getAttribute('data-full');
          if (main && full) main.style.backgroundImage = `url('${full}')`;
          root.querySelectorAll('[data-cd-qv-thumb]').forEach((x) => x.classList.toggle('is-active', x === thumb));
          return;
        }

        // Image nav arrows (prev / next thumbnail)
        const nav = e.target.closest('[data-cd-qv-prev], [data-cd-qv-next]');
        if (nav) {
          const root = nav.closest('[data-cd-qv-root]');
          if (!root) return;
          const thumbs = Array.from(root.querySelectorAll('[data-cd-qv-thumb]'));
          if (thumbs.length < 2) return;
          const active = root.querySelector('[data-cd-qv-thumb].is-active') || thumbs[0];
          const idx = thumbs.indexOf(active);
          const dir = nav.matches('[data-cd-qv-next]') ? 1 : -1;
          const next = thumbs[(idx + dir + thumbs.length) % thumbs.length];
          next.click();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const bd = document.querySelector('[data-cd-qv-backdrop]');
          if (bd && bd.classList.contains('is-open')) this.close();
        }
      });
    },

    // Core dynamic loader: fetch HTML for handle, inject inner HTML into the
    // existing root (no destroy/recreate of the wrapper, no backdrop side-effects).
    // Mirrors the FormatSwap pattern used on the PDP.
    loadInto(root, handle) {
      const url = `${this.localePrefix()}/products/${handle}?view=quickview`;
      const cached = this.cache.get(handle);
      const fetcher = cached
        ? Promise.resolve(cached)
        : fetch(url, {
            credentials: 'same-origin',
            headers: { 'Accept': 'text/html' },
          }).then((r) => (r.ok ? r.text() : Promise.reject(r)));
      return Promise.resolve(fetcher).then((html) => {
        if (!html) throw new Error('empty html');
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const tpl = doc.querySelector('[data-cd-qv-template]');
        if (!tpl) throw new Error('template not found');
        // Drop bind flags so re-init binds the fresh nodes
        root.querySelectorAll('[data-cd-qty-wrap], [data-cd-stand-row]').forEach((el) => {
          delete el.__cdQtyBound;
          delete el.__cdStandBound;
        });
        root.innerHTML = tpl.innerHTML;
        ProductQty.init(root);
        ProductStand.init(root);
        PdpPrice.update(root);
        HoverZoom.init(root);
      });
    },

    open(handle) {
      const root = document.querySelector('[data-cd-qv-root]');
      const bd = document.querySelector('[data-cd-qv-backdrop]');
      if (!root || !bd) {
        window.location.href = `/products/${handle}`;
        return;
      }
      const wasOpen = bd.classList.contains('is-open');
      if (!wasOpen) bd.classList.add('is-loading');
      this.loadInto(root, handle)
        .then(() => {
          bd.classList.remove('is-loading');
          bd.classList.add('is-open');
          bd.setAttribute('aria-hidden', 'false');
          document.body.style.overflow = 'hidden';
        })
        .catch(() => {
          bd.classList.remove('is-loading');
          window.location.href = `/products/${handle}`;
        });
    },

    // Format swap inside an open modal — optimistic active toggle, then
    // smooth innerHTML replace via loadInto. No close/reopen, no flash.
    swapFormat(fmtLink) {
      const handle = fmtLink.getAttribute('data-handle');
      const root = fmtLink.closest('[data-cd-qv-root]');
      if (!handle || !root) return;
      // Optimistic toggle in the Format row
      const row = fmtLink.closest('[data-cd-format-row]');
      if (row) {
        row.querySelectorAll('.variant-option').forEach((o) => o.classList.remove('is-active'));
        fmtLink.classList.add('is-active');
      }
      this.loadInto(root, handle).catch(() => {
        // Fallback: full reopen
        this.open(handle);
      });
    },

    close(opts) {
      const bd = document.querySelector('[data-cd-qv-backdrop]');
      const root = document.querySelector('[data-cd-qv-root]');
      if (bd) {
        bd.classList.remove('is-open');
        bd.setAttribute('aria-hidden', 'true');
      }
      if (root) root.innerHTML = '';
      if (!opts || !opts.keepBodyLock) {
        const drawer = document.querySelector('[data-cart-drawer]');
        const drawerOpen = drawer && drawer.classList.contains('is-open');
        if (!drawerOpen) document.body.style.overflow = '';
      }
    },
  };

  // Handle back/forward browser nav after swap
  window.addEventListener('popstate', () => {
    const section = document.querySelector('[data-cd-pdp-section]');
    if (section) FormatSwap.swap(window.location.pathname + window.location.search);
  });

  // Neutralize Outstock's auto drawer-open trigger via jQuery + remove its native drawer DOM
  function disableOutstockAutoOpen() {
    if (window.jQuery) {
      try {
        window.jQuery('body').off('drawer.open');
        window.jQuery('body').on('drawer.open', function(e) {
          e.stopImmediatePropagation();
          return false;
        });
      } catch (_) {}
    }
    // Remove Outstock native drawer node if present (separate from our [data-cart-drawer])
    document.querySelectorAll('.js-drawer.drawer.drawer--right').forEach((d) => {
      if (!d.hasAttribute('data-cart-drawer')) d.remove();
    });
  }

  /**
   * Newsletter Popup Module (Phase 2 D.5)
   * Trigger 30s timeout OR scroll >50%. sessionStorage 'lc_popup_seen_session' to dedupe per session.
   * 3 close paths: X button, backdrop click, "No thanks" button. Esc also closes.
   * On form.posted_successfully (page reload), force-open in success state.
   */
  const NewsletterPopup = {
    popup: null,
    STORAGE_KEY: 'lc_popup_seen_session',
    SCROLL_THRESHOLD: 0.5,
    TIMEOUT_MS: 30000,
    triggered: false,
    timer: null,
    scrollHandler: null,

    init() {
      this.popup = document.querySelector('[data-cd-newsletter-popup]');
      if (!this.popup) return;

      // Force-open if just submitted successfully (Liquid renders success-flag)
      const isSuccess = !!this.popup.querySelector('[data-cd-popup-success-flag]');

      document.addEventListener('click', (e) => this.onDocumentClick(e));
      document.addEventListener('keydown', (e) => this.onKeydown(e));

      const form = this.popup.querySelector('form');
      if (form) form.addEventListener('submit', () => this.markSeen());

      if (isSuccess) {
        this.open();
        return;
      }

      if (this.hasBeenSeen()) return;

      this.timer = setTimeout(() => this.open(), this.TIMEOUT_MS);
      this.scrollHandler = () => this.onScroll();
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    },

    hasBeenSeen() {
      try { return sessionStorage.getItem(this.STORAGE_KEY) === '1'; } catch (_) { return false; }
    },

    markSeen() {
      try { sessionStorage.setItem(this.STORAGE_KEY, '1'); } catch (_) {}
    },

    onScroll() {
      if (this.triggered) return;
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      if (total > 0 && scrolled / total >= this.SCROLL_THRESHOLD) this.open();
    },

    open() {
      if (this.triggered) return;
      this.triggered = true;
      if (this.timer) clearTimeout(this.timer);
      if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
      this.popup.hidden = false;
      document.body.style.overflow = 'hidden';
      const input = this.popup.querySelector('input[type="email"]');
      if (input) setTimeout(() => input.focus(), 100);
    },

    close() {
      if (!this.popup || this.popup.hidden) return;
      this.popup.hidden = true;
      document.body.style.overflow = '';
      this.markSeen();
    },

    onDocumentClick(e) {
      if (e.target.closest('[data-cd-popup-close]') || e.target.closest('[data-cd-popup-backdrop]')) {
        this.close();
      }
    },

    onKeydown(e) {
      if (e.key === 'Escape' && !this.popup.hidden) this.close();
    },
  };

  /**
   * PDP Preload (Phase 2 follow-up)
   * Silently preloads all thumb full-size images 1.5s after page load, so subsequent
   * thumb clicks are instant (no network round-trip). Avoids competing with LCP.
   */
  const PDPPreload = {
    init() {
      const thumbs = document.querySelectorAll('[data-cd-pdp-thumb]');
      if (thumbs.length < 2) return;
      const preload = () => {
        thumbs.forEach((t) => {
          const full = t.getAttribute('data-full');
          if (!full) return;
          const img = new Image();
          img.decoding = 'async';
          img.src = full;
        });
      };
      // Wait for LCP to settle before kicking off preloads
      if ('requestIdleCallback' in window) {
        requestIdleCallback(preload, { timeout: 2500 });
      } else {
        setTimeout(preload, 1500);
      }
    },
  };

  /**
   * PDP Gallery (Phase 2 follow-up)
   * Mirrors the QuickView thumb pattern: click thumb → swap main <img> src + toggle .is-active.
   * Triggered only on PDP (cd-pdp.liquid) since QV has its own handler bound to [data-cd-qv-*].
   */
  const PDPGallery = {
    init() {
      // Delegate on document so the handler survives FormatSwap re-renders.
      if (document.__cdPDPGalleryBound) return;
      document.__cdPDPGalleryBound = true;

      const activate = (thumb) => {
        const gallery = thumb.closest('[data-cd-pdp-gallery]');
        if (!gallery) return;
        const main = gallery.querySelector('[data-cd-pdp-main]');
        const full = thumb.getAttribute('data-full');
        const alt = thumb.getAttribute('data-alt');
        if (main && full) main.src = full;
        if (main && alt) main.alt = alt;
        gallery.querySelectorAll('[data-cd-pdp-thumb]').forEach((t) => {
          t.classList.toggle('is-active', t === thumb);
        });
      };

      document.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-cd-pdp-thumb]');
        if (thumb) activate(thumb);
      });

      document.addEventListener('keydown', (e) => {
        const thumb = e.target.closest('[data-cd-pdp-thumb]');
        if (thumb && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          activate(thumb);
        }
      });
    },
  };

  /**
   * Bundle Preload (Phase 2 follow-up)
   * Silently preloads all bundle thumb full-size images at idle, so subsequent
   * thumb clicks are instant (no network round-trip). Mirrors PDPPreload.
   */
  const BundlePreload = {
    init() {
      const thumbs = document.querySelectorAll('[data-cd-bundle-thumb]');
      if (thumbs.length < 2) return;
      const preload = () => {
        thumbs.forEach((t) => {
          const full = t.getAttribute('data-full');
          if (!full) return;
          const img = new Image();
          img.decoding = 'async';
          img.src = full;
        });
      };
      if ('requestIdleCallback' in window) {
        requestIdleCallback(preload, { timeout: 2500 });
      } else {
        setTimeout(preload, 1500);
      }
    },
  };

  /**
   * Bundle Gallery (Phase 2 follow-up)
   * Click thumb → swap [data-cd-bundle-main] src + update [data-cd-bundle-label] + toggle .is-active.
   * Mirrors PDPGallery pattern.
   */
  const BundleGallery = {
    init() {
      if (document.__cdBundleGalleryBound) return;
      document.__cdBundleGalleryBound = true;

      const activate = (thumb) => {
        const wrapper = thumb.closest('[data-cd-bundle-thumbs]');
        const section = thumb.closest('[data-cd-bundle-section]');
        if (!wrapper || !section) return;
        const main = section.querySelector('[data-cd-bundle-main]');
        const label = section.querySelector('[data-cd-bundle-label]');
        const full = thumb.getAttribute('data-full');
        const newLabel = thumb.getAttribute('data-label');
        if (main && full) main.src = full;
        if (label && newLabel) label.textContent = newLabel;
        wrapper.querySelectorAll('[data-cd-bundle-thumb]').forEach((t) => {
          t.classList.toggle('is-active', t === thumb);
        });
      };

      document.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-cd-bundle-thumb]');
        if (thumb) activate(thumb);
      });
    },
  };

  /**
   * Hover Zoom (Phase 2 follow-up)
   * Cursor-tracking zoom on main product images: PDP, QuickView, Bundle.
   * Detects <img> child (transform-origin) vs background-image (background-position).
   * Re-callable scoped to a root for QuickView re-injection.
   */
  const HoverZoom = {
    init(root) {
      const scope = root || document;
      scope.querySelectorAll('[data-cd-zoom]').forEach((el) => {
        if (el.__cdZoomBound) return;
        el.__cdZoomBound = true;

        const findImg = () => el.querySelector('img');

        el.addEventListener('mouseenter', () => {
          el.classList.add('is-zooming');
        });
        el.addEventListener('mousemove', (e) => {
          const rect = el.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          const img = findImg();
          if (img) {
            img.style.transformOrigin = `${x}% ${y}%`;
          } else {
            el.style.backgroundPosition = `${x}% ${y}%`;
          }
        });
        el.addEventListener('mouseleave', () => {
          el.classList.remove('is-zooming');
          const img = findImg();
          if (img) img.style.transformOrigin = '';
          else el.style.backgroundPosition = '';
        });
      });
    },
  };

  /**
   * Locale Switcher (Phase 2 D.5 follow-up)
   * Close <details data-locale-wrap> on outside click or Esc.
   */
  const LocaleSwitcher = {
    init() {
      const wrap = document.querySelector('[data-locale-wrap]');
      if (!wrap) return;
      document.addEventListener('click', (e) => {
        if (wrap.open && !wrap.contains(e.target)) wrap.open = false;
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && wrap.open) wrap.open = false;
      });
    },
  };

  /**
   * Country Block FR (Phase 3 A.1)
   * Detect visitor from France, show non-blocking modal with option to continue anyway.
   * Dismissal stored in localStorage for the session.
   */
  const CountryBlockFR = {
    init() {
      if (document.__cdCountryBlockBound) return;
      document.__cdCountryBlockBound = true;

      // Check if already dismissed in this session
      if (localStorage.getItem('cd_fr_block_dismissed') === '1') return;

      this.detectAndShow();
    },

    async detectAndShow() {
      const isFrance = await this.isFranceCountry();
      if (!isFrance) return;

      const root = document.querySelector('[data-cd-country-block-root]');
      const backdrop = document.querySelector('[data-cd-country-block-backdrop]');
      if (!root || !backdrop) return;

      // Show modal
      root.removeAttribute('hidden');
      backdrop.removeAttribute('aria-hidden');
      backdrop.classList.add('is-open');

      // Bind close handlers
      const closeBtn = root.querySelector('[data-cd-country-block-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.close());
      }
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) this.close();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    },

    close() {
      const root = document.querySelector('[data-cd-country-block-root]');
      const backdrop = document.querySelector('[data-cd-country-block-backdrop]');
      if (root) root.setAttribute('hidden', '');
      if (backdrop) {
        backdrop.setAttribute('aria-hidden', 'true');
        backdrop.classList.remove('is-open');
      }
      localStorage.setItem('cd_fr_block_dismissed', '1');
    },

    async isFranceCountry() {
      try {
        // Strategy 1: Try Shopify.country native object (if available in theme context)
        if (typeof Shopify !== 'undefined' && Shopify.country) {
          return Shopify.country === 'FR';
        }

        // Strategy 2: Try Shopify API endpoint for country suggestions
        const response = await fetch('/browsing_context_suggestions.json?country_code=FR', {
          signal: AbortSignal.timeout(3000),
        });
        if (response.ok) {
          const data = await response.json();
          // If the endpoint returns country data, check for FR
          if (data && data.country && data.country.code === 'FR') {
            return true;
          }
        }

        // Strategy 3: Check navigator.language as fallback
        const lang = navigator.language || navigator.userLanguage || '';
        return lang.startsWith('fr-');
      } catch (err) {
        // If detection fails, don't show modal (err on side of caution)
        if (err.name !== 'AbortError') {
          console.warn('CountryBlockFR detection failed:', err);
        }
        return false;
      }
    },
  };

  function bootAll() {
    CartDrawer.init();
    CollectionFilters.init();
    PriceFilter.init();
    CartPage.init();
    DrawerLines.init();
    ProductQty.init();
    ProductTabs.init();
    ProductStand.init();
    FormatSwap.init();
    RelatedShuffle.init();
    AddToCart.init();
    QuickView.init();
    FAQAccordion.init();
    ArticleShare.init();
    SearchSort.init();
    NewsletterPopup.init();
    LocaleSwitcher.init();
    CountryBlockFR.init();
    PDPGallery.init();
    PDPPreload.init();
    BundleGallery.init();
    BundlePreload.init();
    HoverZoom.init();
    disableOutstockAutoOpen();
    // jQuery may load later, retry once
    setTimeout(disableOutstockAutoOpen, 1500);
    window.CartPage = CartPage;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }
})();
